/**
 * Writing an XscpData payload to the clipboard.
 *
 * Two things make this awkward, and both are load-bearing:
 *
 * 1. Webflow reads the payload from the `application/json` clipboard type — but
 *    `navigator.clipboard.write()` **rejects** that MIME type without the unsanitized-formats
 *    flag. The way through is to intercept a synthetic `copy` event and call
 *    `clipboardData.setData()` for both `application/json` and `text/plain`, which means the
 *    deprecated `document.execCommand('copy')` is the only path that works.
 *
 * 2. The payload must start with `{` exactly. Webflow reports a misleading "clipboard is
 *    empty" for anything else — a BOM, a leading space, trailing bytes.
 *
 * Safari does not support this at all.
 */

export type CopyResult =
  | { ok: true; bytes: number }
  | { ok: false; reason: 'safari' | 'malformed' | 'failed' };

export function isSafari(): boolean {
  const ua = navigator.userAgent;
  return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
}

/**
 * Strip anything before the opening brace and after the matching close.
 *
 * Mirrors what `webflow-clipboard` does. Kept inline rather than taken as a dependency so the
 * failure mode is visible here — this is the code that decides whether a paste works at all.
 */
export function normaliseXscp(input: string): string | null {
  let s = input.replace(/^﻿/, '').trimStart();
  const start = s.indexOf('{');
  if (start === -1) return null;
  s = s.slice(start);

  // Walk to the matching brace so trailing bytes cannot leak in.
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === '"') inString = !inString;
    if (inString) continue;
    if (ch === '{') depth++;
    else if (ch === '}') {
      depth--;
      if (depth === 0) return s.slice(0, i + 1);
    }
  }
  return null;
}

export async function copyXscp(json: string): Promise<CopyResult> {
  if (isSafari()) return { ok: false, reason: 'safari' };

  const payload = normaliseXscp(json);
  if (!payload) return { ok: false, reason: 'malformed' };

  const onCopy = (e: ClipboardEvent): void => {
    e.preventDefault();
    e.clipboardData?.setData('application/json', payload);
    e.clipboardData?.setData('text/plain', payload);
  };

  document.addEventListener('copy', onCopy, true);
  try {
    const ta = document.createElement('textarea');
    ta.value = payload;
    ta.setAttribute('aria-hidden', 'true');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    // Deprecated, and the only API that will carry application/json.
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok ? { ok: true, bytes: payload.length } : { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed' };
  } finally {
    document.removeEventListener('copy', onCopy, true);
  }
}
